import { inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { type IBaseCard, TaxprepT1Card } from "@app/views-interfaces";
import { ITaxReturnStatus } from "@app/views-interfaces/tax-return-status-model.interface";
import { debounceTime, interval, Subject, share, skip, of, take, concatAll } from "rxjs";
import { CardDisplayService } from "../../shared/card-display/card-display.service";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import { FiltersService } from "../filters-service/filters.service";
import { HelpersService } from "../helpers-service/helpers.service";
import { MappingOfStatusesService } from "../mapping-of-statuses-service/mapping-of-statuses.service";
import { TaxReturnStatusesService } from "./../tax-return-statuses-service/tax-return-statuses.service";

export interface StatisticModel {
    stepId: string;
    statusId: string;
    state: string;
    count: number;
    totalDaysInWorkflow: number;
    lastYearCount: number;
    warningCount: number;
    alertCount: number;
}

@Injectable({ providedIn: "root" })
export class CountersService {
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly filtersService = inject(FiltersService);
    private readonly helpersService = inject(HelpersService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly taxReturnStatusesService = inject(TaxReturnStatusesService);

    isLoadingColumn = {};
    private readonly countersUpdatedSource = new Subject();
    countersUpdated$ = this.countersUpdatedSource.asObservable();
    statisticsModel: StatisticModel[];
    aYearAgo: number;

    constructor() {
        this.dashboardsService.dashboardChangeEvent$.pipe(takeUntilDestroyed()).subscribe(async () => {
            this.statisticsModel = [];
        });
        this.dashboardsService.currentDashboardChanged$.pipe(takeUntilDestroyed()).subscribe(async () => {
            this.refreshStatistics();
        });
        this.filtersService.filtersChangedEvent$.pipe(takeUntilDestroyed()).subscribe(async () => {
            await this.refreshStatistics();
            this.countersUpdatedSource.next(null);
        });

        const cardChanged$ = this.cardDisplayService.cardChangedEvent$.pipe(share());

        of(cardChanged$.pipe(take(1)), cardChanged$.pipe(skip(1), debounceTime(2000)))
            .pipe(concatAll(), takeUntilDestroyed())
            .subscribe(async () => {
                await this.refreshStatistics();
                this.countersUpdatedSource.next(null);
            });

        this.aYearAgo = this.helpersService.convertMillisecondsToSeconds(Date.now()) - HelpersService.secondsInAYear;
        interval(60000)
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                this.aYearAgo = this.helpersService.convertMillisecondsToSeconds(Date.now()) - HelpersService.secondsInAYear;
            });
    }

    async refreshStatistics() {
        const statuses = await this.taxReturnStatusesService.getTaxReturnStatusesList();
        const cardDisplayModel = await this.cardDisplayService.getModel();
        const allCards = await this.cardDisplayService.loadAllCards();
        const statusIds = statuses.map((y) => y.Id);
        this.statisticsModel = cardDisplayModel
            .filter((cardModel) => statusIds.includes(cardModel.statusId))
            .map((cardModel) => {
                const statusIdsFromStep = this.mappingOfStatusesService.getStatusIdsFromStepId(cardModel.stepId);
                const filteredCards = this.filtersService.filterCards(cardModel.cards as TaxprepT1Card[], statusIdsFromStep, {
                    ignoreCurrentView: true,
                });

                return {
                    stepId: cardModel.stepId,
                    statusId: cardModel.statusId,
                    state: cardModel.state,
                    count: filteredCards.reduce((sum, card) => {
                        return sum + this.helpersService.getClientFileCount(card);
                    }, 0),
                    totalDaysInWorkflow: filteredCards.reduce((sum, card) => {
                        return sum + this.cardDisplayService.calcCardFieldLabels(card, statusIdsFromStep).daysSinceReady;
                    }, 0),
                    lastYearCount: allCards.filter((card) => {
                        if ("lastYearStatusesHistory" in card && card.lastYearStatusesHistory.length > 0) {
                            const lastYearStatus = this.getCurrentStatusFromHistory(card.lastYearStatusesHistory, statuses);
                            const lastYearStep = this.mappingOfStatusesService.getStepFromStatusId(lastYearStatus);
                            if (lastYearStep?.Id === cardModel.stepId) {
                                return true;
                            }
                        }
                        return false;
                    }).length,
                    warningCount: filteredCards.filter((card) => {
                        const cardStatusId = this.cardDisplayService.getCardDisplayStatusId(card);
                        const result = this.cardDisplayService.calcCardFieldLabels(card, [cardStatusId]);
                        return (
                            !((cardModel.state === "completed" && result.isEFileStatusAlert) || result.isStepAlert || result.isDeadlineAlert) &&
                            (result.isStepWarning || result.isDeadlineWarning)
                        );
                    }).length,
                    alertCount: filteredCards.filter((card) => {
                        const cardStatusId = this.cardDisplayService.getCardDisplayStatusId(card);
                        const result = this.cardDisplayService.calcCardFieldLabels(card, [cardStatusId]);
                        return (cardModel.state === "completed" && result.isEFileStatusAlert) || result.isStepAlert || result.isDeadlineAlert;
                    }).length,
                };
            });
    }

    getCurrentStatusFromHistory(statusesHistory: IBaseCard["statusesHistory"], returnStatusList: ITaxReturnStatus[]) {
        let currentStatus;
        let currentStatusDate = 0;
        const highest = {
            statusDate: 0,
            statusId: "",
        };

        const rollForwarded = returnStatusList.find((status) => status.TaxAppStatusId === "0");

        for (const status of statusesHistory) {
            if (highest.statusDate <= status.statusDate) {
                highest.statusId = status.statusId;
            }
            if (
                status.statusDate < this.aYearAgo &&
                (status.statusDate > currentStatusDate || (status.statusDate === currentStatusDate && status.statusId === rollForwarded?.Id))
            ) {
                currentStatus = status.statusId;
                currentStatusDate = status.statusDate;
            }
        }

        return currentStatus ?? highest.statusId;
    }
}
